import { useEffect, useState } from "react";
import { LuExternalLink, LuLoaderCircle } from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import { StripeApi } from "../../api/stripeApi";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useSubscriptionActions } from "../../core_stores/useSubscriptionStore";
import { SubscriptionProduct } from "../../model/subscriptionProduct";
import { useAuthModal } from "../../providers/AuthModalProvid";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useSubscriptionsModal } from "../../providers/SubscriptionsModalProvider";
import { trackEvent } from "../../utils/analyticsUtils";
import { usePromptInputActions } from "../../view_stores/usePromptInputStore";
import SubscriptionsModalItem from "./components/SubscriptionsModalItem";
import SubscriptionsModalLayout from "./components/SubscriptionsModalLayout";
import SubscriptionsRemainingTokens from "./components/SubscriptionsRemainingTokens";

export default function SubscriptionsModal({
  onClose,
}: {
  onClose?: () => void;
}) {
  const isUserSignedIn = useIsUserSignedIn();

  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  useEffect(() => {
    if (isUserSignedIn) {
      reloadSubscriptionsInfo();
    }

    // Prevent background scroll when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isUserSignedIn]);

  return (
    <>
      <SubscriptionsModalLayout onClose={onClose}>
        <Content />
      </SubscriptionsModalLayout>
      <Tooltip
        id="remaining-tokens-tooltip"
        place="top"
        className="max-w-[95%]"
        opacity={1.0}
      >
        Unused tokens will not accumulate or roll over from month to month. Each
        billing cycle resets your token balance.
      </Tooltip>
    </>
  );
}

function Content() {
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();

  const { show: showAuthModal } = useAuthModal();
  const { close: closeSubscriptionsModal } = useSubscriptionsModal();
  const { saveInLocalStorage: savePromptInLocalStorage } =
    usePromptInputActions();

  const [products, setProducts] = useState<SubscriptionProduct[] | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isCheckoutSessionLoading, setIsCheckoutSessionLoading] =
    useState(false);

  const { showSnackbar } = useSnackbar();

  const loadProducts = async () => {
    setProducts(null);
    const products = await StripeApi.loadProducts();
    setProducts(products);
  };

  const handleSelect = async (product: SubscriptionProduct) => {
    if (!isUserSignedIn) {
      await showAuthModal();
      if (product.id === "dream") {
        closeSubscriptionsModal();
      }
      return;
    }
    setSelectedProduct(product.id);
    setIsCheckoutSessionLoading(true);
    try {
      const checkoutSession = await StripeApi.createCheckoutSession(
        product.price_id
      );
      trackEvent("purchase_initiated");
      savePromptInLocalStorage();
      window.location.href = checkoutSession.url;
    } catch (e) {
      showSnackbar(
        "Error creating checkout session. Try again later or contact us on: support@darvin.dev",
        {
          type: "error",
          duration: null,
        }
      );
    }
    setIsCheckoutSessionLoading(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    loadProducts();
  }, [isUserSignedIn]);

  return (
    <div
      className="absolute top-0 left-0 w-full h-full 
    pt-6 pb-8 flex flex-col gap-6 overflow-y-auto"
    >
      {products === null ? (
        <div
          className="flex flex-col items-center justify-center 
        h-full w-full gap-3 px-8"
        >
          <LuLoaderCircle size={24} className="animate-spin" />
          <p>Loading plans...</p>
        </div>
      ) : (
        <>
          <div className="px-8 flex flex-col gap-6 flex-1">
            <h2 className="text-4xl font-bold text-white text-center">
              Pricing
            </h2>
            {isCompactView && isUserSignedIn && (
              <SubscriptionsRemainingTokens />
            )}
            <div
              className={`${
                isCompactView
                  ? "grid-cols-1"
                  : products.length === 3
                  ? "grid-cols-3"
                  : products.length === 4
                  ? "grid-cols-4"
                  : "grid-cols-5"
              } flex-1 grid gap-3 items-center`}
            >
              {products.map((product, idx) => (
                <SubscriptionsModalItem
                  key={idx}
                  product={product}
                  isDisabled={isCheckoutSessionLoading}
                  isLoading={selectedProduct === product.id}
                  onSelect={() => handleSelect(product)}
                />
              ))}
            </div>
            {!isCompactView && isUserSignedIn && (
              <SubscriptionsRemainingTokens />
            )}
          </div>
          {isUserSignedIn && (
            <div className="flex flex-col items-center justify-center gap-4 px-2">
              <RedirectToUserPanel />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RedirectToUserPanel() {
  const { navigateToUserPanel } = useSubscriptionActions();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const onClick = async () => {
    setIsRedirecting(true);
    await navigateToUserPanel();
    setIsRedirecting(false);
  };

  return (
    <button
      id="subscriptions-modal-redirect-to-user-panel"
      onClick={onClick}
      className="text-primary-blue dark:text-blue-400 
        text-[min(14px,3.2vw)] hover:underline bg-transparent border-none 
        cursor-pointer p-0 flex justify-center items-center gap-1"
      disabled={isRedirecting}
    >
      <span>Manage your payment preferences and check past payments</span>
      <LuExternalLink className="ml-1" />
      {isRedirecting && <LuLoaderCircle className="animate-spin" />}
    </button>
  );
}
